import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  deleteDoc,
} from 'firebase/firestore';
import { supabase } from './supabaseClient';
import defaultUser from './user-default.png';
import UserProfile from './UserProfile';

function MenteeConnections({ setPage, setCallParams }) {
  const [userId, setUserId] = useState(null);
  const [pendingMentors, setPendingMentors] = useState([]);
  const [acceptedMentors, setAcceptedMentors] = useState([]);
  const [rejectedMentors, setRejectedMentors] = useState([]);
  const [tab, setTab] = useState('accepted');
  const [viewingUserId, setViewingUserId] = useState(null);

  const isDark = document.documentElement.classList.contains('dark');
  const theme = isDark ? 'dark' : 'light';

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged(user => {
      if (user) setUserId(user.uid);
      else setUserId(null);
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!userId) return;

    const q = query(collection(db, 'mentorRequests'), where('requesterId', '==', userId));
    const unsubscribe = onSnapshot(q, async snapshot => {
      const pending = [];
      const accepted = [];
      const rejected = [];

      const processingPromises = snapshot.docs.map(async docSnap => {
        const data = docSnap.data();
        const mentorId = data.mentorId;
        const mentorDoc = await getDoc(doc(db, 'users', mentorId));
        if (!mentorDoc.exists()) return;

        const mentorData = mentorDoc.data();
        const mentorName = mentorData.fullName || mentorData.email || 'Unknown';

        const { data: supa } = supabase.storage
          .from('profile-pictures')
          .getPublicUrl(`profile-pictures/${mentorId}/profile.jpg`);
        const photoURL = supa?.publicUrl || defaultUser;

        const item = {
          chatId: docSnap.id,
          mentorId,
          mentorName,
          photoURL,
          status: data.status,
          createdAt: data.createdAt?.toDate?.() || null,
        };

        if (data.status === 'accepted') accepted.push(item);
        else if (data.status === 'pending') pending.push(item);
        else if (data.status === 'rejected') rejected.push(item);
      });

      await Promise.all(processingPromises);
      setPendingMentors(pending);
      setAcceptedMentors(accepted);
      setRejectedMentors(rejected);
    });

    return () => unsubscribe();
  }, [userId]);

  const deleteChat = async (chatId) => {
    if (!window.confirm('Are you sure you want to delete this connection?')) return;
    try {
      await deleteDoc(doc(db, 'mentorRequests', chatId));
      alert('Deleted successfully.');
    } catch (err) {
      console.error(err);
      alert('Failed to delete.');
    }
  };

  if (!userId) return <p>Please log in to see your connections.</p>;
  if (viewingUserId) {
    return <UserProfile userId={viewingUserId} setPage={() => setViewingUserId(null)} />;
  }

  const buttonStyle = (selected, color) => ({
    backgroundColor: selected
      ? color
      : theme === 'dark'
      ? '#2d2d2d'
      : '#eaeaea',
    color: selected ? '#fff' : theme === 'dark' ? '#ccc' : '#333',
    padding: '10px 20px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    letterSpacing: 0.3,
    boxShadow: selected ? '0 2px 10px rgba(0,0,0,0.15)' : 'none',
    transition: 'all 0.25s ease',
  });

  const sectionTitleStyle = {
    fontSize: '1.4rem',
    fontWeight: 'bold',
    marginBottom: '20px',
    textAlign: 'center',
  };

  const containerBg =
    theme === 'dark'
      ? 'linear-gradient(145deg, #0f0f0f, #1a1a1a)'
      : 'linear-gradient(145deg, #f8fbff, #e6f0ff)';

  const cardBase = {
    borderRadius: '16px',
    padding: '18px',
    transition: 'all 0.25s ease',
    cursor: 'pointer',
    textAlign: 'center',
    background: theme === 'dark' ? '#181818' : '#ffffff',
    boxShadow:
      theme === 'dark'
        ? '0 2px 8px rgba(255,255,255,0.06)'
        : '0 4px 16px rgba(0,0,0,0.08)',
  };

  const renderMentorCards = (list, color) => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '24px',
      }}
    >
      {list.map((m) => (
        <div
          key={m.chatId}
          onClick={() => setViewingUserId(m.mentorId)}
          style={cardBase}
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-5px)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
        >
          <img
            src={m.photoURL}
            alt={m.mentorName}
            style={{
              width: 90,
              height: 90,
              borderRadius: '50%',
              objectFit: 'cover',
              marginBottom: 12,
              border: `3px solid ${color}`,
            }}
          />
          <div
            style={{
              fontWeight: 600,
              fontSize: '1.05rem',
              color: theme === 'dark' ? '#fafafa' : '#222',
            }}
          >
            {m.mentorName}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div
      style={{
        padding: '50px 25px',
        minHeight: '100vh',
        background: containerBg,
        color: theme === 'dark' ? '#ffffff' : '#000000',
        fontFamily: 'Poppins, sans-serif',
        transition: 'background 0.3s ease',
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1
          style={{
            fontSize: '2.2rem',
            fontWeight: 700,
            color: theme === 'dark' ? '#4facfe' : '#007BFF',
          }}
        >
          Mentee Connections
        </h1>
        <p style={{ opacity: 0.8, fontSize: '1rem', marginTop: '8px' }}>
          Your mentorship journey — all in one place.
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '15px',
          marginBottom: '35px',
          flexWrap: 'wrap',
        }}
      >
        <button
          onClick={() => setTab('accepted')}
          style={buttonStyle(tab === 'accepted', '#3B82F6')}
        >
          Accepted
        </button>
        <button
          onClick={() => setTab('pending')}
          style={buttonStyle(tab === 'pending', '#F59E0B')}
        >
          Pending
        </button>
        <button
          onClick={() => setTab('rejected')}
          style={buttonStyle(tab === 'rejected', '#DC2626')}
        >
          Rejected
        </button>
      </div>

      {tab === 'accepted' && (
        <>
          <h2 style={{ ...sectionTitleStyle, color: '#3B82F6' }}>Accepted Mentors</h2>
          {acceptedMentors.length > 0 ? (
            renderMentorCards(acceptedMentors, '#3B82F6')
          ) : (
            <p style={{ textAlign: 'center' }}>No accepted mentors yet.</p>
          )}
        </>
      )}

      {tab === 'pending' && (
        <>
          <h2 style={{ ...sectionTitleStyle, color: '#F59E0B' }}>Pending Requests</h2>
          {pendingMentors.length > 0 ? (
            <div style={{ marginTop: 20 }}>
              {pendingMentors.map((m) => (
                <div
                  key={m.chatId}
                  style={{
                    ...cardBase,
                    marginBottom: 20,
                    background: theme === 'dark' ? '#202020' : '#fffbe6',
                  }}
                >
                  <div style={{ fontWeight: 'bold' }}>{m.mentorName}</div>
                  <div style={{ color: '#888', fontSize: '0.9em' }}>
                    Request ID: {m.chatId.slice(0, 6)}
                  </div>
                  <button
                    onClick={() => deleteChat(m.chatId)}
                    style={{
                      marginTop: '10px',
                      backgroundColor: '#DC2626',
                      color: 'white',
                      border: 'none',
                      padding: '8px 14px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ textAlign: 'center' }}>No pending requests.</p>
          )}
        </>
      )}

      {tab === 'rejected' && (
        <>
          <h2 style={{ ...sectionTitleStyle, color: '#DC2626' }}>Rejected Requests</h2>
          {rejectedMentors.length > 0 ? (
            <div style={{ marginTop: 20 }}>
              {rejectedMentors.map((m) => (
                <div
                  key={m.chatId}
                  style={{
                    ...cardBase,
                    marginBottom: 20,
                    background: theme === 'dark' ? '#202020' : '#fff',
                  }}
                >
                  <div style={{ fontWeight: 'bold' }}>{m.mentorName}</div>
                  <div style={{ color: '#888', fontSize: '0.9em' }}>
                    Request ID: {m.chatId.slice(0, 6)}
                  </div>
                  <button
                    onClick={() => deleteChat(m.chatId)}
                    style={{
                      marginTop: '10px',
                      backgroundColor: '#DC2626',
                      color: 'white',
                      border: 'none',
                      padding: '8px 14px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ textAlign: 'center' }}>No rejected requests.</p>
          )}
        </>
      )}
    </div>
  );
}

export default MenteeConnections;
